<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import * as echarts from 'echarts'
import type { StatisticsChartData } from '@/components/dashboard/dashboard.interfaces.ts'
import { isNull } from 'lodash'

const props = defineProps<{
  data: StatisticsChartData
}>()

const chartRef = ref<HTMLElement | null>(null)
let chartInstance: echarts.ECharts | null = null

function initChart(): void {
  if (isNull(chartRef.value)) {
    return
  }

  chartInstance = echarts.init(chartRef.value)
  updateChart()
}

function updateChart(): void {
  if (isNull(chartInstance) || isNull(props.data)) {
    return
  }

  const options: echarts.EChartsOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: props.data.series.map(s => s.name)},
    grid: { left: '2%', right: '2%', bottom: '15%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: props.data.categories },
    yAxis: [
      {
        type: 'value',
        name: 'Chats/Contacts',
      },
      {
        type: 'value',
        name: 'Conversion (%)',
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}%'
        },
        splitLine: {
          show: false
        }
      }
    ],
    series: props.data.series.map(series => ({
      ...series,
      type: 'line',
      smooth: true,
      yAxisIndex: series.name === 'Conversion' ? 1 : 0
    })) as echarts.SeriesOption[]
  }

  chartInstance.setOption(options)
}

function handleResize(): void {
  if (chartInstance) {
    chartInstance.resize()
  }
}

onMounted(() => {
  initChart()
  window.addEventListener('resize', handleResize)
})

watch(() => props.data, () => {
  updateChart()
}, { deep: true })

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
  }
})
</script>

<template>
  <div ref="chartRef" class="chart"></div>
</template>

<style scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
